import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Train,
  Plane,
  Bus,
  Car,
  Clock,
  Home,
  Bookmark,
  MapPin,
  Users,
  User,
  Loader2,
  ExternalLink,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import { usePlanStore } from '../store/planStore';
import { useLocation as useLocationContext } from '../contexts/LocationContext';
import api from '../services/api';
import FloatingSupport from '../components/FloatingSupport';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const TRANSPORT_TYPES = [
  { value: 'flight', label: 'Flights', icon: Plane, color: 'teal' },
  { value: 'train', label: 'Trains', icon: Train, color: 'amber' },
  { value: 'bus', label: 'Buses', icon: Bus, color: 'emerald' },
  { value: 'cab', label: 'Cabs', icon: Car, color: 'rose' },
];

export default function Transport() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentPlan } = usePlanStore();
  const { formatCurrency, currency, countryCode } = useLocationContext();
  
  const origin = searchParams.get('origin') || currentPlan?.origin || '';
  const destination = searchParams.get('destination') || currentPlan?.destination || '';
  const travelStyle = searchParams.get('style') || currentPlan?.travel_style || 'comfort';
  
  const [transportType, setTransportType] = useState(null); // null = show AI recommendation first
  const [recommendations, setRecommendations] = useState([]);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(true);
  const [apiSource, setApiSource] = useState(null);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  
  // Regional booking platforms (loaded based on user location)
  const [bookingPlatforms, setBookingPlatforms] = useState(null);
  
  // Load booking platforms based on user's country
  useEffect(() => {
    const loadBookingPlatforms = async () => {
      try {
        const data = await api.getBookingPlatforms(countryCode);
        if (data.success) {
          setBookingPlatforms(data.platforms);
        }
      } catch (error) {
        console.error('Failed to load booking platforms:', error);
      }
    };
    loadBookingPlatforms();
  }, [countryCode]);
  
  // Get booking links based on transport type
  const getBookingLinks = (type) => {
    if (!bookingPlatforms) {
      // Fallback to global defaults
      return {
        flight: [
          { name: 'Skyscanner', url: 'https://www.skyscanner.com/' },
          { name: 'Google Flights', url: 'https://www.google.com/flights' }
        ],
        train: [
          { name: 'Trainline', url: 'https://www.thetrainline.com/' },
          { name: 'Rome2Rio', url: 'https://www.rome2rio.com/' }
        ],
        bus: [
          { name: 'FlixBus', url: 'https://www.flixbus.com/' },
          { name: 'Rome2Rio', url: 'https://www.rome2rio.com/' }
        ],
        cab: [
          { name: 'Uber', url: 'https://www.uber.com/' },
          { name: 'Bolt', url: 'https://bolt.eu/' }
        ]
      }[type] || [];
    }
    
    const typeMapping = {
      flight: 'flights',
      train: 'trains',
      bus: 'buses',
      cab: 'cabs'
    };
    
    return bookingPlatforms[typeMapping[type]] || [];
  };
  
  // Load AI recommendation on mount
  useEffect(() => {
    if (origin && destination) {
      loadAIRecommendation();
    }
  }, [origin, destination]);
  
  // Load transport options when type is selected
  useEffect(() => {
    if (destination && transportType) loadRecommendations();
  }, [destination, transportType]);
  
  const loadAIRecommendation = async () => {
    try {
      setAiLoading(true);
      const data = await api.getAITransportRecommendation(origin, destination, travelStyle);
      if (data.success && data.recommendation) {
        setAiRecommendation(data.recommendation);
        // Auto-select the recommended transport type
        setTransportType(data.recommendation.primary_recommendation);
      }
    } catch (error) {
      console.error('AI recommendation error:', error);
    } finally {
      setAiLoading(false);
    }
  };
  
  const loadRecommendations = async () => {
    try {
      setLoading(true);
      setNoDataAvailable(false);
      
      if (transportType === 'flight') {
        // Use real Amadeus API for flights
        const departureDate = new Date();
        departureDate.setDate(departureDate.getDate() + 7); // Search for flights 7 days from now
        const formattedDate = departureDate.toISOString().split('T')[0];
        
        const data = await api.searchFlights(origin, destination, formattedDate, { adults: 1 });
        
        if (data.success && data.flights && data.flights.length > 0) {
          setApiSource(data.source);
          // Transform API response to our format
          const formatted = data.flights.map((flight, index) => ({
            id: flight.id || `flight_${index}`,
            provider: flight.airline,
            departure: flight.departure?.time?.split(' ')[1] || flight.departure?.time || '06:00',
            arrival: flight.arrival?.time?.split(' ')[1] || flight.arrival?.time || '09:00',
            duration: flight.duration,
            price: flight.price?.total,
            rating: 4.5,
            type: index === 0 ? 'cheapest' : index === 1 ? 'recommended' : 'fastest',
            stops: flight.stops,
            non_stop: flight.non_stop,
            seats_available: flight.seats_available,
            segments: flight.segments
          }));
          setRecommendations(formatted);
        } else {
          // No flights found - show not available
          setNoDataAvailable(true);
          setRecommendations([]);
        }
      } else if (transportType === 'cab') {
        // Cabs are not available for long-distance routes - redirect to third party
        setNoDataAvailable(true);
        setRecommendations([]);
      } else if (transportType === 'train' || transportType === 'bus') {
        // For train/bus, show "not available" and redirect to third party
        setNoDataAvailable(true);
        setRecommendations([]);
      }
    } catch (err) {
      console.error('Transport load error:', err);
      setNoDataAvailable(true);
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  };
  
  const getTypeLabel = (type, index) => {
    if (index === 0) return { label: '💰 Cheapest', color: 'bg-emerald-100 text-emerald-700' };
    if (index === 1) return { label: '⭐ Recommended', color: 'bg-teal-100 text-teal-700' };
    return { label: '⚡ Fast', color: 'bg-amber-100 text-amber-700' };
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="text-center">
            <h1 className="font-bold text-gray-900">Transport Options</h1>
            <p className="text-xs text-gray-500">{origin} → {destination}</p>
          </div>
          
          <div className="w-8" />
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* AI Recommendation Section */}
        {aiLoading ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white"
            style={{ borderRadius: '1.5rem' }}
          >
            <div className="flex items-center gap-4">
              <Loader2 size={32} className="animate-spin" />
              <div>
                <h2 className="text-xl font-bold">Panchi AI is thinking...</h2>
                <p className="text-teal-100">Analyzing the best transport option for your trip</p>
              </div>
            </div>
          </motion.div>
        ) : aiRecommendation && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 text-white shadow-xl"
            style={{ borderRadius: '1.5rem' }}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur flex items-center justify-center" style={{ borderRadius: '1rem' }}>
                {aiRecommendation.primary_recommendation === 'train' ? <Train size={28} /> :
                 aiRecommendation.primary_recommendation === 'flight' ? <Plane size={28} /> :
                 aiRecommendation.primary_recommendation === 'bus' ? <Bus size={28} /> :
                 <Car size={28} />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 bg-white/20 text-xs font-bold" style={{ borderRadius: '9999px' }}>
                    🤖 AI RECOMMENDATION
                  </span>
                  {aiRecommendation.confidence === 'high' && (
                    <span className="px-2 py-0.5 bg-emerald-400/30 text-xs font-bold" style={{ borderRadius: '9999px' }}>
                      ✓ HIGH CONFIDENCE
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-black capitalize">{aiRecommendation.primary_recommendation}</h2>
                <p className="text-teal-100 mt-1">{aiRecommendation.reasoning}</p>
              </div>
            </div>
            
            {/* Key Stats */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 bg-white/10 backdrop-blur" style={{ borderRadius: '0.75rem' }}>
                <Clock size={16} className="mb-1 text-teal-200" />
                <p className="text-xs text-teal-200">Duration</p>
                <p className="font-bold">{aiRecommendation.estimated_duration}</p>
              </div>
              <div className="p-3 bg-white/10 backdrop-blur" style={{ borderRadius: '0.75rem' }}>
                <span className="text-lg mb-1">💰</span>
                <p className="text-xs text-teal-200">Est. Cost</p>
                <p className="font-bold text-sm">{aiRecommendation.estimated_cost_inr}</p>
              </div>
              <div className="p-3 bg-white/10 backdrop-blur" style={{ borderRadius: '0.75rem' }}>
                <span className="text-lg mb-1">⭐</span>
                <p className="text-xs text-teal-200">Best For</p>
                <p className="font-bold text-sm truncate">{travelStyle}</p>
              </div>
            </div>
            
            {/* Route Details - Multi-leg journey */}
            {aiRecommendation.route_details?.legs && aiRecommendation.route_details.legs.length > 0 && (
              <div className="p-3 bg-white/10 backdrop-blur mb-4" style={{ borderRadius: '0.75rem' }}>
                <p className="font-bold text-sm mb-3">🗺️ Route Details</p>
                <div className="space-y-2">
                  {aiRecommendation.route_details.legs.map((leg, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="w-6 h-6 bg-white/20 flex items-center justify-center text-xs font-bold" style={{ borderRadius: '50%' }}>
                        {i + 1}
                      </span>
                      <div className="flex-1">
                        <span className="font-semibold capitalize">{leg.mode}</span>
                        <span className="text-teal-200 mx-1">•</span>
                        <span className="text-teal-100">{leg.from} → {leg.to}</span>
                        {leg.duration && <span className="text-teal-200 ml-1">({leg.duration})</span>}
                      </div>
                      {leg.cost_estimate && (
                        <span className="text-xs bg-white/20 px-2 py-1" style={{ borderRadius: '9999px' }}>
                          {leg.cost_estimate}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {aiRecommendation.route_details.total_cost && (
                  <div className="mt-3 pt-3 border-t border-white/20 flex justify-between text-sm">
                    <span className="text-teal-200">Total Journey</span>
                    <span className="font-bold">{aiRecommendation.route_details.total_duration} • {aiRecommendation.route_details.total_cost}</span>
                  </div>
                )}
              </div>
            )}
            
            {/* No Direct Route Note */}
            {aiRecommendation.no_direct_route_note && (
              <div className="p-3 bg-amber-500/20 backdrop-blur mb-4 flex items-start gap-2" style={{ borderRadius: '0.75rem' }}>
                <AlertCircle size={18} className="text-amber-300 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-100">{aiRecommendation.no_direct_route_note}</p>
              </div>
            )}
            
            {/* Pro Tips */}
            {aiRecommendation.pro_tips && aiRecommendation.pro_tips.length > 0 && (
              <div className="p-3 bg-white/10 backdrop-blur mb-4" style={{ borderRadius: '0.75rem' }}>
                <p className="font-bold text-sm mb-2">💡 Pro Tips</p>
                <ul className="text-sm text-teal-100 space-y-1">
                  {aiRecommendation.pro_tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-teal-300">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Book Now Button */}
            <a
              href={thirdPartyLinks[aiRecommendation.primary_recommendation === 'multi-mode' ? 'flight' : aiRecommendation.primary_recommendation]?.[Object.keys(thirdPartyLinks[aiRecommendation.primary_recommendation === 'multi-mode' ? 'flight' : aiRecommendation.primary_recommendation] || {})[0]] || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3.5 bg-white text-teal-600 font-bold text-center flex items-center justify-center gap-2 hover:bg-teal-50 transition-colors"
              style={{ borderRadius: '9999px' }}
            >
              Book {aiRecommendation.primary_recommendation === 'train' ? 'on IRCTC' : 
                    aiRecommendation.primary_recommendation === 'flight' ? 'on MakeMyTrip' : 
                    aiRecommendation.primary_recommendation === 'bus' ? 'on RedBus' : 'Now'}
              <ExternalLink size={16} />
            </a>
          </motion.div>
        )}
        
        {/* Alternatives Section */}
        {aiRecommendation?.alternatives && aiRecommendation.alternatives.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-gray-900">Other Options</h3>
            {aiRecommendation.alternatives.map((alt, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 bg-white border border-gray-100 shadow-sm flex items-center justify-between"
                style={{ borderRadius: '1rem' }}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center ${
                    alt.mode === 'train' ? 'bg-amber-100' :
                    alt.mode === 'flight' ? 'bg-teal-100' :
                    alt.mode === 'bus' ? 'bg-emerald-100' : 'bg-rose-100'
                  }`} style={{ borderRadius: '0.75rem' }}>
                    {alt.mode === 'train' ? <Train size={20} className="text-amber-600" /> :
                     alt.mode === 'flight' ? <Plane size={20} className="text-teal-600" /> :
                     alt.mode === 'bus' ? <Bus size={20} className="text-emerald-600" /> :
                     <Car size={20} className="text-rose-600" />}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 capitalize">{alt.mode}</p>
                    <p className="text-xs text-gray-500">{alt.when_to_choose}</p>
                  </div>
                </div>
                <button
                  onClick={() => setTransportType(alt.mode)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-gray-200 transition-colors"
                  style={{ borderRadius: '9999px' }}
                >
                  Explore
                </button>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Transport Types Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {TRANSPORT_TYPES.map((type) => {
            const isSelected = transportType === type.value;
            const isRecommended = aiRecommendation?.primary_recommendation === type.value;
            return (
              <button
                key={type.value}
                onClick={() => setTransportType(type.value)}
                className={`flex items-center gap-2 px-5 py-3 font-semibold whitespace-nowrap transition-all ${
                  isSelected 
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30' 
                    : 'bg-white border-2 border-gray-100 text-gray-600 hover:border-teal-200'
                }`}
                style={{ borderRadius: '9999px' }}
                data-testid={`transport-${type.value}`}
              >
                <type.icon size={18} />
                {type.label}
                {isRecommended && !isSelected && (
                  <span className="ml-1 text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5" style={{ borderRadius: '9999px' }}>AI Pick</span>
                )}
              </button>
            );
          })}
        </div>
        
        {/* Route Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow-lg"
          style={{ borderRadius: '1.5rem' }}
        >
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-sm text-gray-500 mb-1">From</p>
              <p className="text-xl font-bold text-gray-900">{origin}</p>
            </div>
            <div className="w-12 h-12 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '50%' }}>
              {transportType === 'train' ? <Train size={20} className="text-teal-600" /> :
               transportType === 'bus' ? <Bus size={20} className="text-teal-600" /> :
               transportType === 'cab' ? <Car size={20} className="text-teal-600" /> :
               <Plane size={20} className="text-teal-600" />}
            </div>
            <div className="flex-1 text-right">
              <p className="text-sm text-gray-500 mb-1">To</p>
              <p className="text-xl font-bold text-gray-900">{destination}</p>
            </div>
          </div>
        </motion.div>
        
        {/* Recommendations */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4">Available Options</h2>
          
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 size={32} className="animate-spin text-teal-500 mb-4" />
              <p className="text-gray-500">Finding best options...</p>
            </div>
          ) : noDataAvailable ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <div className="w-20 h-20 bg-amber-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '1.5rem' }}>
                <AlertCircle size={40} className="text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {transportType === 'cab' 
                  ? 'Inter-city Cabs Not Available' 
                  : transportType === 'train'
                    ? 'Train Search Not Available'
                    : transportType === 'bus'
                      ? 'Bus Search Not Available'
                      : 'No Flights Found'}
              </h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                {transportType === 'cab' 
                  ? `Long-distance cab service from ${origin} to ${destination} is not available in our system. Book through popular cab services below.`
                  : transportType === 'train'
                    ? `Train schedules from ${origin} to ${destination} need to be checked on IRCTC or other platforms.`
                    : transportType === 'bus'
                      ? `Bus routes from ${origin} to ${destination} can be booked through popular bus booking platforms.`
                      : `No direct flights found from ${origin} to ${destination}. Try alternative airports or dates.`}
              </p>
              
              {/* Third-party booking links */}
              {transportType === 'cab' && (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <a 
                    href={thirdPartyLinks.cab.uber}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-black text-white font-semibold hover:bg-gray-800 transition-colors"
                    style={{ borderRadius: '9999px' }}
                  >
                    Book on Uber <ExternalLink size={16} />
                  </a>
                  <a 
                    href={thirdPartyLinks.cab.ola}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors"
                    style={{ borderRadius: '9999px' }}
                  >
                    Book on Ola <ExternalLink size={16} />
                  </a>
                </div>
              )}
              
              {transportType === 'train' && (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <a 
                    href={thirdPartyLinks.train.irctc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-colors"
                    style={{ borderRadius: '9999px' }}
                  >
                    Book on IRCTC <ExternalLink size={16} />
                  </a>
                  <a 
                    href={thirdPartyLinks.train.confirmtkt}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                    style={{ borderRadius: '9999px' }}
                  >
                    Check on ConfirmTkt <ExternalLink size={16} />
                  </a>
                </div>
              )}
              
              {transportType === 'bus' && (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <a 
                    href={thirdPartyLinks.bus.redbus}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors"
                    style={{ borderRadius: '9999px' }}
                  >
                    Book on RedBus <ExternalLink size={16} />
                  </a>
                  <a 
                    href={thirdPartyLinks.bus.abhibus}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors"
                    style={{ borderRadius: '9999px' }}
                  >
                    Book on AbhiBus <ExternalLink size={16} />
                  </a>
                </div>
              )}
              
              {transportType === 'flight' && (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <a 
                    href={`https://www.google.com/travel/flights?q=flights+from+${encodeURIComponent(origin)}+to+${encodeURIComponent(destination)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors"
                    style={{ borderRadius: '9999px' }}
                  >
                    Search on Google Flights <ExternalLink size={16} />
                  </a>
                  <a 
                    href={`https://www.makemytrip.com/flight/search?itinerary=${encodeURIComponent(origin)}-${encodeURIComponent(destination)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors"
                    style={{ borderRadius: '9999px' }}
                  >
                    Search on MakeMyTrip <ExternalLink size={16} />
                  </a>
                </div>
              )}
            </motion.div>
          ) : (
            <div className="space-y-3">
              {apiSource && transportType === 'flight' && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <span className={`w-2 h-2 ${apiSource === 'amadeus_live' ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ borderRadius: '50%' }} />
                  {apiSource === 'amadeus_live' ? 'Live flight data from Amadeus' : 'Showing sample data'}
                </div>
              )}
              {recommendations.map((rec, index) => {
                const typeLabel = getTypeLabel(rec.type, index);
                return (
                  <motion.div
                    key={rec.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="p-5 bg-white/80 backdrop-blur border border-gray-100 shadow hover:shadow-lg hover:border-teal-200 transition-all"
                    style={{ borderRadius: '1.25rem' }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-teal-100 flex items-center justify-center text-lg" style={{ borderRadius: '0.75rem' }}>
                          {transportType === 'flight' ? '✈️' : transportType === 'train' ? '🚂' : transportType === 'bus' ? '🚌' : '🚗'}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{rec.provider}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            {rec.non_stop !== undefined && (
                              <span className={`px-2 py-0.5 text-xs ${rec.non_stop ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`} style={{ borderRadius: '9999px' }}>
                                {rec.non_stop ? 'Non-stop' : `${rec.stops} stop${rec.stops > 1 ? 's' : ''}`}
                              </span>
                            )}
                            {rec.seats_available && (
                              <span className="text-xs text-gray-400">{rec.seats_available} seats left</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {typeLabel && (
                        <span className={`px-3 py-1 text-xs font-bold ${typeLabel.color}`} style={{ borderRadius: '9999px' }}>
                          {typeLabel.label}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="text-lg font-bold text-gray-900">{rec.departure}</p>
                          <p className="text-xs text-gray-500">{origin}</p>
                        </div>
                        <div className="flex items-center gap-2 px-4 py-1 bg-gray-100" style={{ borderRadius: '9999px' }}>
                          <Clock size={14} className="text-gray-500" />
                          <span className="text-sm text-gray-600">{rec.duration}</span>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-gray-900">{rec.arrival}</p>
                          <p className="text-xs text-gray-500">{destination}</p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-2xl font-black text-teal-600">{formatCurrency(typeof rec.price === 'number' ? rec.price : parseFloat(rec.price) || 0)}</p>
                        <button 
                          className="mt-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 transition-all flex items-center gap-1"
                          style={{ borderRadius: '9999px' }}
                          data-testid={`book-${rec.id}`}
                        >
                          Book <ExternalLink size={14} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Info Note */}
        <div className="p-4 bg-teal-50 border border-teal-200 text-center" style={{ borderRadius: '1rem' }}>
          <p className="text-teal-700 text-sm">
            {transportType === 'flight' 
              ? "✈️ Real-time flight data powered by Amadeus. Prices may vary at booking."
              : "💡 Prices are estimates. You'll be redirected to the provider's website to complete booking."
            }
          </p>
        </div>
      </main>
      
      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Home size={22} />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/saved" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Bookmark size={22} />
            <span className="text-xs font-medium">Saved</span>
          </Link>
          <Link 
            to="/planning"
            className="w-14 h-14 -mt-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 flex items-center justify-center"
            style={{ borderRadius: '50%' }}
          >
            <MapPin size={24} />
          </Link>
          <Link to="/community" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Users size={22} />
            <span className="text-xs font-medium">Community</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <User size={22} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
      
      <FloatingSupport />
    </div>
  );
}
